// My Day — where the ops admin lands, and the answer to "what is left?".
//
// The workspace used to open on Counter Sales: a data-entry form. Nothing told
// the admin what was still outstanding, so the only way to find out was to
// visit all four screens and read them. This screen leads with the two things
// they are accountable for at the end of a day:
//
//   1. the cash — money in, money out, what should be in the drawer right now
//   2. the work — what is still waiting, in the order it has to be done
//
// Every card goes straight to the screen that clears it. Nothing waiting says
// so plainly, because "I am done" is an answer worth showing.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowRight,
  FiCheckCircle,
  FiShoppingBag,
  FiCreditCard,
  FiTruck,
  FiCornerUpLeft,
  FiAlertTriangle,
  FiFileText,
  FiTrendingUp,
  FiTrendingDown,
} from 'react-icons/fi';
import OpsLayout from '../../components/layout/OpsLayout.jsx';
import { opsCounts, useOpsDay } from '../../hooks/useOpsDay';
import { ROUTES } from '../../constants/app.constants';

const rupees = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '₹0';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const TONES = {
  act:  { card: 'border-violet-200', soft: 'bg-violet-50', text: 'text-violet-700', btn: 'bg-violet-600 hover:bg-violet-700' },
  pay:  { card: 'border-rose-200', soft: 'bg-rose-50', text: 'text-rose-700', btn: 'bg-rose-600 hover:bg-rose-700' },
  warn: { card: 'border-amber-200', soft: 'bg-amber-50', text: 'text-amber-700', btn: 'bg-amber-500 hover:bg-amber-600' },
};

const TaskCard = ({ icon: Icon, tone = 'act', count, title, body, money, action, to }) => {
  const t = TONES[tone] || TONES.act;
  const navigate = useNavigate();
  return (
    <div className={`flex flex-col rounded-2xl border ${t.card} bg-white p-5 shadow-sm`}>
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${t.soft}`}>
          <Icon className={`h-5 w-5 ${t.text}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-black leading-none tabular-nums text-slate-900">{count}</span>
            <h3 className="text-sm font-bold text-slate-700">{title}</h3>
          </div>
          <p className="mt-1.5 text-xs leading-relaxed text-slate-500">{body}</p>
        </div>
      </div>
      {money ? (
        <span className={`mt-3 inline-flex w-fit rounded-full ${t.soft} ${t.text} px-2.5 py-1 text-xs font-bold`}>
          {money}
        </span>
      ) : null}
      <button
        onClick={() => navigate(to)}
        className={`mt-4 inline-flex items-center justify-center gap-2 rounded-xl ${t.btn} py-2.5 text-sm font-bold text-white transition-all`}
      >
        {action} <FiArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
};

const CashLine = ({ label, value, tone = 'slate', strong }) => {
  const colors = {
    slate: 'text-slate-500', in: 'text-emerald-600', out: 'text-rose-500',
  };
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className={`${strong ? 'font-bold text-slate-700' : 'text-slate-500'}`}>{label}</span>
      <span className={`tabular-nums ${strong ? 'font-bold' : 'font-semibold'} ${colors[tone]}`}>
        {tone === 'out' && Number(value) > 0 ? '− ' : ''}{rupees(value)}
      </span>
    </div>
  );
};

// The body is its OWN component rendered as OpsLayout's CHILD, and that is
// load-bearing: OpsDayProvider is mounted INSIDE OpsLayout, so a useOpsDay()
// called in a component that RENDERS OpsLayout sits outside the provider and
// reads the default context — day forever null, no error, an always-empty
// screen while the nav badges (inside the layout) show real numbers. That
// exact bug shipped. Consume the day only from within the layout's children.
const OpsHomeBody = () => {
  const { day, payables, loading, error } = useOpsDay();
  const c = opsCounts(day, payables);

  const collections = day?.collections || {};
  const balance = Number(day?.balance);
  const sales = day?.sales || {};
  const counter = day?.counter || {};
  const dispatch = day?.dispatch || {};
  const expenses = day?.expenses || {};

  const tasks = [
    c.toEnter > 0 && {
      key: 'enter', icon: FiShoppingBag, tone: 'act', count: c.toEnter,
      title: 'Supervisor sales to enter',
      body: 'Supervisors sold these directly. They are not in the books until you enter them.',
      action: 'Enter them', to: ROUTES.OPS_COUNTER,
    },
    c.toApprove > 0 && {
      key: 'approve', icon: FiCreditCard, tone: 'act', count: c.toApprove,
      title: 'Payments to approve',
      body: 'Check what the customer actually paid. Nothing can be dispatched until you approve it.',
      action: 'Check payments', to: ROUTES.OPS_PAYMENTS,
    },
    c.toDispatch > 0 && {
      key: 'dispatch', icon: FiTruck, tone: 'act', count: c.toDispatch,
      title: 'Ready to hand over',
      body: 'Paid for and still with us. Dispatch completes the sale.',
      action: 'Open dispatch', to: ROUTES.OPS_DISPATCH,
    },
    c.refundsDue > 0 && {
      key: 'refunds', icon: FiCornerUpLeft, tone: 'pay', count: c.refundsDue,
      title: 'Refunds to hand back',
      body: 'Cash owed to customers on returned parts.',
      money: `${rupees(c.refundsValue)} to pay`,
      action: 'Pay refunds', to: ROUTES.EXPENSES,
    },
    c.vendorsDue > 0 && {
      key: 'vendors', icon: FiFileText, tone: 'pay', count: c.vendorsDue,
      title: 'Vendors to settle',
      body: 'Outside-market vendors still owed on parts already sold.',
      money: `${rupees(c.vendorsValue)} owed`,
      action: 'See payables', to: ROUTES.EXPENSES,
    },
    c.commitmentsDue > 0 && {
      key: 'money', icon: FiAlertTriangle, tone: 'warn', count: c.commitmentsDue,
      title: 'Due today',
      body: 'Chit instalments, salary, rent and other commitments falling due.',
      money: `${rupees(c.dueTodayValue)} due`,
      action: 'Settle them', to: ROUTES.EXPENSES,
    },
  ].filter(Boolean);

  const clear = !loading && tasks.length === 0;

  return (
      <div className="space-y-5">
        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {/* ── The drawer ─────────────────────────────────────────────────── */}
        <div className="grid gap-4 lg:grid-cols-3">
          <div className={`rounded-3xl p-6 text-white shadow-lg lg:col-span-1 ${
            balance < 0
              ? 'bg-gradient-to-br from-rose-600 to-rose-700 shadow-rose-500/20'
              : 'bg-gradient-to-br from-violet-600 to-indigo-700 shadow-violet-500/20'
          }`}>
            <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
              Should be in hand now
            </p>
            <p className="mt-2 text-4xl font-black tabular-nums">
              {Number.isFinite(balance) ? rupees(balance) : '—'}
            </p>
            <p className="mt-2 text-xs text-white/70">
              Money in today, minus refunds handed back and expenses paid.
            </p>
            {balance < 0 && (
              <p className="mt-3 rounded-xl bg-white/15 px-3 py-2 text-xs font-semibold">
                More has gone out than came in today — check the expenses.
              </p>
            )}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
              <FiTrendingUp className="h-4 w-4 text-emerald-500" /> Money in
            </p>
            <div className="mt-3">
              {[
                ['Sales paid in full', collections.sales_full],
                ['Advances taken', collections.advances],
                ['Balances collected', collections.balances],
                ['Counter sales', collections.counter],
              ].filter(([, v]) => Number(v) > 0).map(([l, v]) => (
                <CashLine key={l} label={l} value={v} tone="in" />
              ))}
              {!Number(collections.total_in) && (
                <p className="py-2 text-sm text-slate-400">Nothing collected yet today.</p>
              )}
              <div className="mt-1 border-t border-slate-100 pt-1.5">
                <CashLine label="Total in" value={collections.total_in} tone="in" strong />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="flex items-center gap-2 text-sm font-bold text-slate-900">
              <FiTrendingDown className="h-4 w-4 text-rose-500" /> Money out
            </p>
            <div className="mt-3">
              <CashLine label="Refunds handed back" value={collections.refunds_paid} tone="out" />
              <CashLine label="Expenses logged" value={expenses.total} tone="out" />
              <div className="mt-1 border-t border-slate-100 pt-1.5">
                <CashLine
                  label="Still to pay today"
                  value={c.dueTodayValue}
                  tone={c.dueTodayValue > 0 ? 'out' : 'slate'}
                  strong
                />
              </div>
              {c.overdue > 0 && (
                <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-1 text-[11px] font-bold text-rose-600">
                  <FiAlertTriangle className="h-3 w-3" />
                  {c.overdue} overdue · {rupees(c.overdueValue)}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── What is left ───────────────────────────────────────────────── */}
        {clear && (
          <div className="rounded-2xl border border-emerald-200 bg-white p-10 text-center shadow-sm">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50">
              <FiCheckCircle className="h-7 w-7 text-emerald-500" />
            </div>
            <p className="text-lg font-black text-slate-800">The day is clear</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
              Nothing to enter, approve, dispatch or pay out. New work shows up here as it comes in.
            </p>
          </div>
        )}

        {tasks.length > 0 && (
          <div>
            <h2 className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">
              Still to do — {tasks.length} thing{tasks.length === 1 ? "" : "s"}, in the order the money moves
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {tasks.map((t) => <TaskCard key={t.key} {...t} to={t.to} />)}
            </div>
          </div>
        )}

        {/* ── What already happened ──────────────────────────────────────── */}
        <div>
          <h2 className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">
            Done today
          </h2>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              { label: 'Sales approved', value: sales.count ?? 0, sub: rupees(sales.value), icon: FiCreditCard },
              { label: 'Counter sales', value: counter.count ?? 0, sub: rupees(counter.total), icon: FiShoppingBag },
              { label: 'Dispatched', value: dispatch.today_count ?? 0, sub: rupees(dispatch.today_value), icon: FiTruck },
              { label: 'Expense entries', value: expenses.count ?? 0, sub: rupees(expenses.total), icon: FiFileText },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-1.5 flex items-center gap-2 text-slate-400">
                  <s.icon className="h-4 w-4" />
                  <span className="truncate text-[11px] font-semibold uppercase tracking-wide">{s.label}</span>
                </div>
                <p className="text-2xl font-black leading-none tabular-nums text-slate-900">{s.value}</p>
                <p className="mt-1 text-xs text-slate-400">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
  );
};

const OpsHomePage = () => (
  <OpsLayout
    title="My Day"
    subtitle="Today's cash position and anything still waiting on you."
  >
    <OpsHomeBody />
  </OpsLayout>
);

export default OpsHomePage;
